/**
 * Remote storage operations using storage service
 */

import storageService from "../../data/Services/storageService.jsx";
import logger from "../../utils/logger";
import {
  serializeWorkspace,
  deserializeWorkspace,
} from "../core/serialization.js";

/**
 * Save desktop remotely to marketdata service
 * @param {Object} chartsStore - The charts store state
 * @param {string} name - Desktop name
 * @param {Object} options - Save options
 * @param {string} options.id - Desktop ID (for updates)
 * @returns {Promise<Object>} - Saved desktop data with id
 */
export const saveDesktopRemote = async (chartsStore, name, options = {}) => {
  try {
    if (!name || name.trim() === "") {
      throw new Error("Desktop name is required");
    }

    // Serialize the workspace
    const desktopData = await serializeWorkspace(chartsStore);
    const content = JSON.stringify(desktopData);

    // Save to remote service
    // Server expects: -1 for new desktop, or existing chartid for update
    // If options.id is provided, we're updating an existing desktop
    // If options.id is null/undefined, use -1 to create new desktop
    const chartId =
      options.id !== undefined && options.id !== null ? options.id : -1;

    const savedData = await storageService.saveDesktop(
      chartId,
      name.trim(),
      content,
    );

    logger.workspace.success(`Desktop "${name}" salvo no servidor`, {
      name: name,
      id: savedData?.chartid || savedData || chartId,
    });

    return {
      id: savedData?.chartid || savedData || chartId,
      name: name.trim(),
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to save desktop remotely:", error);
    logger.workspace.error(`Falha ao salvar desktop "${name}"`, {
      error: error.message,
      name: name,
    });
    throw error;
  }
};

/**
 * Load desktop from remote storage
 * @param {string} id - Desktop ID
 * @param {Function} loadCharts - Function to load charts into store
 * @returns {Promise<boolean>} - Success status
 */
export const loadDesktopRemote = async (id, loadCharts) => {
  try {
    if (!id) {
      throw new Error("Desktop ID is required");
    }

    console.log("Loading desktop from remote:", id);

    // Fetch desktop data from service
    const remoteData = await storageService.getDesktop(id);

    if (!remoteData || !remoteData.content) {
      throw new Error("Desktop not found or invalid data");
    }

    // Parse the content
    const desktopData = JSON.parse(remoteData.content);

    // Deserialize and load
    const success = deserializeWorkspace(desktopData, loadCharts);

    if (success) {
      logger.workspace.success(
        `Desktop "${remoteData.name}" carregado com sucesso`,
        {
          name: remoteData.name,
          id: id,
        },
      );
    }

    return success;
  } catch (error) {
    console.error("Failed to load desktop remotely:", error);
    logger.workspace.error("Falha ao carregar desktop do servidor", {
      error: error.message,
      id: id,
    });
    throw error;
  }
};

/**
 * List all desktops from remote storage
 * @returns {Promise<Array>} - Array of desktop metadata
 */
export const listDesktopsRemote = async () => {
  try {
    console.log("Fetching desktop list from server...");

    const desktopList = await storageService.getDesktopList();

    if (!desktopList || !Array.isArray(desktopList)) {
      console.warn("Invalid desktop list received from server");
      return [];
    }

    // Transform the data to a consistent format
    const transformedList = desktopList.map((desktop) => ({
      id: desktop.chartid,
      name: desktop.name || "Unnamed Desktop",
      timestamp:
        desktop.timestamp || desktop.created || new Date().toISOString(),
      // Parse content to get additional metadata if available
      metadata: (() => {
        try {
          if (desktop.content) {
            const parsed = JSON.parse(desktop.content);
            return {
              version: parsed.version,
              chartsCount: parsed.charts?.length || 0,
              selectedTab: parsed.selectedTab,
            };
          }
        } catch {
          // Ignore parse errors
        }
        return {};
      })(),
    }));

    console.log(`Fetched ${transformedList.length} desktops from server`);

    return transformedList;
  } catch (error) {
    console.error("Failed to list desktops:", error);
    logger.workspace.error("Falha ao listar desktops do servidor", {
      error: error.message,
    });
    throw error;
  }
};

/**
 * Delete a desktop from remote storage
 * @param {string} id - Desktop ID
 * @returns {Promise<boolean>} - Success status
 */
export const deleteDesktopRemote = async (id) => {
  try {
    if (!id) {
      throw new Error("Desktop ID is required");
    }

    console.log("Deleting desktop from remote:", id);

    await storageService.deleteDesktop(id);

    logger.workspace.success("Desktop removido do servidor", {
      id: id,
    });

    return true;
  } catch (error) {
    console.error("Failed to delete desktop:", error);
    logger.workspace.error("Falha ao remover desktop do servidor", {
      error: error.message,
      id: id,
    });
    throw error;
  }
};

/**
 * Rename a desktop in remote storage
 * Note: This uses saveDesktop with existing ID to update the name
 * @param {string} id - Desktop ID
 * @param {string} newName - New desktop name
 * @returns {Promise<boolean>} - Success status
 */
export const renameDesktopRemote = async (id, newName) => {
  try {
    if (!id) {
      throw new Error("Desktop ID is required");
    }

    if (!newName || newName.trim() === "") {
      throw new Error("New desktop name is required");
    }

    console.log("Renaming desktop:", id, "to", newName);

    // Get the existing desktop
    const existingData = await storageService.getDesktop(id);

    if (!existingData || !existingData.content) {
      throw new Error("Desktop not found");
    }

    // Save with the new name but same content
    await storageService.saveDesktop(id, newName.trim(), existingData.content);

    logger.workspace.success(`Desktop renomeado para "${newName}"`, {
      id: id,
      newName: newName.trim(),
    });

    return true;
  } catch (error) {
    console.error("Failed to rename desktop:", error);
    logger.workspace.error("Falha ao renomear desktop", {
      error: error.message,
      id: id,
      newName: newName,
    });
    throw error;
  }
};

/**
 * Duplicate a desktop in remote storage
 * @param {string} id - Desktop ID to duplicate
 * @param {string} newName - Name for the duplicated desktop
 * @returns {Promise<Object>} - Duplicated desktop data with new id
 */
export const duplicateDesktopRemote = async (id, newName) => {
  try {
    if (!id) {
      throw new Error("Desktop ID is required");
    }

    if (!newName || newName.trim() === "") {
      throw new Error("New desktop name is required");
    }

    console.log("Duplicating desktop:", id, "as", newName);

    // Get the existing desktop
    const existingData = await storageService.getDesktop(id);

    if (!existingData || !existingData.content) {
      throw new Error("Desktop not found");
    }

    // Save as a new desktop (-1 means create new)
    const savedData = await storageService.saveDesktop(
      -1,
      newName.trim(),
      existingData.content,
    );

    console.log("Duplicate response from server:", savedData);

    logger.workspace.success(`Desktop duplicado como "${newName}"`, {
      originalId: id,
      newId: savedData?.chartid || savedData,
      newName: newName.trim(),
    });

    return {
      id: savedData?.chartid,
      name: newName.trim(),
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to duplicate desktop:", error);
    logger.workspace.error("Falha ao duplicar desktop", {
      error: error.message,
      id: id,
      newName: newName,
    });
    throw error;
  }
};

/**
 * Check if a desktop name already exists
 * @param {string} name - Desktop name to check
 * @param {string} excludeId - Optional ID to exclude from check (for rename operations)
 * @returns {Promise<Object|null>} - Desktop object if exists, null otherwise
 */
export const desktopNameExists = async (name, excludeId = null) => {
  try {
    const desktopList = await listDesktopsRemote();

    const found = desktopList.find(
      (desktop) =>
        desktop.name.toLowerCase() === name.toLowerCase() &&
        desktop.id !== excludeId,
    );

    return found || null;
  } catch (error) {
    console.error("Failed to check desktop name:", error);
    // Return null on error to allow operation to proceed
    return null;
  }
};
